// "What we found": the verdict up front, then every reason as a short card under a
// plain heading, grouped by what it means (proof, warning signs, good signs, notes).
import AiMark, { isAiReason } from '../../components/ui/AiMark.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { VERDICT_WHY } from './inboxShared.jsx';

// A short headline for each reason (the detail stays underneath)
const HEADLINE = {
  spoofed: 'Fake sender address', lookalike: 'Lookalike domain', known_scam: 'Already a known scam',
  bank_not_theirs: 'Bank details that aren’t yours', reply_elsewhere: 'Replies go somewhere else',
  impersonation: 'Uses one of your people’s names', claims_staff: 'Poses as your staff',
  attendee_list_scam: 'Selling your attendee list', same_as_known_scam: 'Same as a confirmed scam',
  honeypot_hit: 'Sent to a planted contact', honeypot_expected: 'Expected use of a planted list',
  likely_scam: 'Says it’s you, but isn’t from you', ai_risk: 'AI rates it as risky',
  free_mail: 'Sent from a free email account', sender_unknown: 'Sender isn’t one of yours',
  link_unknown: 'Link isn’t to your website', link_text_mismatch: 'Link goes somewhere else',
  new_domain: 'Newly registered domain', threat_listed: 'On a phishing or malware list', threat_listed_minor: 'On a spam list',
  phone_unknown: 'Phone number isn’t yours', unusual_service: 'Unusual email service',
  scam_pattern: 'Looks like a known scam', ai_scam_pattern: 'Reads like a known scam',
  like_suspected_scam: 'Like an email flagged elsewhere', ai_bank_change: 'Asks to pay new bank details',
  ai_credentials: 'Asks for a login or code', ai_urgent_payment: 'Pushes for quick payment',
  logo_retired: 'Uses your old logo', logo_outdated: 'Uses another version of your logo',
  logo_retired_own: 'Uses your previous logo', logo_outdated_own: 'Uses an older logo', logo_current: 'Uses your current logo',
  contents_theirs: 'Everything in it is yours',
  stamp_mismatch: 'A copied verification stamp', stamp_unknown: 'A fake verification stamp', stamp_revoked: 'Carries a stamp you cancelled',
  stamp_reused: 'Stamp made for another email', stamp_match: 'Its stamp matches your email',
  verified: 'Really from you', witnessed: 'We received this same email ourselves', witnessed_inline: 'Check the reply address',
  origin_unknown: 'Forwarded as text', origin_not_proven: 'Origin couldn’t be proven',
  sender_not_listed: 'Sender not on your People list', services_not_learned: 'Email services not learned yet',
  known_scam_number: 'Known scam number', caller_not_listed: 'Caller isn’t one of your people',
  number_not_theirs: 'Number isn’t yours', number_theirs: 'One of your numbers (can be faked)',
  asked_payment: 'Asked for money or card details', asked_details: 'Asked for a password or details',
  asked_buy_data: 'Offered to sell a list', likely_unsolicited: 'Doesn’t match anything of yours',
  nothing_to_check: 'Nothing to check',
  supplier_named: 'Named one of your suppliers', supplier_claim_mismatch: 'Borrows a supplier’s name',
  supplier_number: 'Your supplier’s number (can be faked)', not_a_supplier: 'Not one of your suppliers',
  question_not_theirs: 'What they asked about isn’t yours', detail_not_theirs: 'Not one of yours', details_theirs: 'All of it is yours',
};

const GROUPS = [
  { key: 'scam', title: 'Proof it’s a scam', tone: 'bad', icon: '✕' },
  { key: 'caution', title: 'Warning signs', tone: 'warn', icon: '!' },
  { key: 'good', title: 'Good signs', tone: 'good', icon: '✓' },
  { key: 'info', title: 'Worth knowing', tone: 'info', icon: 'i' },
];

const VERDICT_HEAD = {
  scam: { tone: 'bad', icon: '✕', title: 'Scam' },
  suspicious: { tone: 'bad', icon: '!', title: 'Likely scam' },
  cannot_verify: { tone: 'warn', icon: '?', title: 'Not confirmed' },
  likely_genuine: { tone: 'good', icon: '✓', title: 'Looks genuine' },
  verified: { tone: 'good', icon: '✓', title: 'Genuine' },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** One line about how strong the verdict is, from the reasons behind it. */
function strength(verdict, counts) {
  if (verdict === 'scam') return plural(counts.scam, 'definite sign', 'definite signs') + (counts.caution ? `, ${plural(counts.caution, 'warning sign', 'warning signs')}` : '');
  if (verdict === 'suspicious') return plural(counts.caution, 'warning sign', 'warning signs');
  if (verdict === 'verified') return 'Proven to come from you';
  if (verdict === 'likely_genuine') return 'Everything in it is yours — not proof of where it came from';
  return counts.caution ? plural(counts.caution, 'thing to watch', 'things to watch') : 'Nothing proves it either way';
}

export default function Findings({ analysis, recheck, rechecking, canRecheck }) {
  const counts = Object.fromEntries(GROUPS.map((g) => [g.key, analysis.reasons.filter((r) => r.severity === g.key).length]));
  const head = VERDICT_HEAD[analysis.verdict] || VERDICT_HEAD.cannot_verify;
  return (
    <section className="card section findings" aria-labelledby="findings-h">
      <div className={`findings-head tone-${head.tone}`}>
        <span className="findings-icon" aria-hidden="true">{head.icon}</span>
        <div className="findings-head-text">
          <h2 id="findings-h">What we found: {head.title}</h2>
          <p className="findings-strength">{strength(analysis.verdict, counts)}</p>
          <p className="findings-why">{VERDICT_WHY[analysis.verdict]}</p>
        </div>
        {canRecheck && (
          <button type="button" className="btn btn-secondary btn-small findings-recheck" disabled={rechecking} onClick={recheck}>
            {rechecking ? 'Checking…' : 'Check again'}
          </button>
        )}
      </div>

      {analysis.reasons.length === 0 ? (
        <p className="muted findings-empty">Nothing in it matches your records or a known scam, so only you can say what it is.</p>
      ) : GROUPS.filter((g) => counts[g.key]).map((g) => (
        <div key={g.key} className="findings-group">
          <h3 className={`findings-group-title tone-${g.tone}`}>{g.title} <span className="findings-count">{counts[g.key]}</span></h3>
          <ul className="findings-cards">
            {analysis.reasons.filter((r) => r.severity === g.key).map((r, i) => (
              <li key={`${r.code}-${i}`} className={`finding-card tone-${g.tone}`}>
                <span className="finding-icon" aria-hidden="true">{g.icon}</span>
                <div>
                  <strong>{isAiReason(r) && <AiMark />}{HEADLINE[r.code] || g.title}</strong>
                  <p>{r.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="muted small findings-foot">Checked {fmtDateTime(analysis.checked_at)} against your records at the time.</p>
    </section>
  );
}
